/**
 * Typed FCM push-notification envelope (DCCD-2371 contract).
 *
 * The backend emits a flat snake_case `data` map. parseEnvelope lifts the known
 * envelope keys into typed camelCase fields and leaves every other key (order_id,
 * reason, ...) untouched in `params`, so the client maps params 1:1 into ICU
 * placeholders with no casing transforms at the boundary.
 */

export const SUPPORTED_SCHEMA_VERSION = '1'

export type ReceiverType = 'A' | 'C' | 'O' | 'V' | 'VSA'

export interface PushEnvelope {
  schemaVersion: string
  event: string
  group?: string
  section?: string
  tag?: string
  severity?: string
  area?: string
  userId?: string
  pushUrl?: string
  receiverType?: ReceiverType
  timestamp?: string
  hash?: string
  actionUrl?: string
  params: Record<string, string>
}

// snake_case (wire) -> camelCase (typed field). Everything not listed stays in params.
const LIFTED: Record<string, Exclude<keyof PushEnvelope, 'params'>> = {
  schema_version: 'schemaVersion',
  event: 'event',
  group: 'group',
  section: 'section',
  tag: 'tag',
  severity: 'severity',
  area: 'area',
  user_id: 'userId',
  push_url: 'pushUrl',
  receiver_type: 'receiverType',
  timestamp: 'timestamp',
  hash: 'hash',
  action_url: 'actionUrl',
}

// Reserved client namespace (_*) and FCM transport noise (gcm.*, google.*) never
// surface as fields or params.
const isIgnoredKey = (key: string): boolean =>
  key.startsWith('_') || key.startsWith('gcm.') || key.startsWith('google.')

export function parseEnvelope(raw?: Record<string, string> | null): PushEnvelope | null {
  if (!raw || typeof raw !== 'object') return null

  const event = typeof raw.event === 'string' ? raw.event.trim() : ''
  if (!event) return null

  const envelope: PushEnvelope = {
    schemaVersion: '',
    event,
    params: {},
  }

  for (const [key, value] of Object.entries(raw)) {
    if (value == null) continue
    if (isIgnoredKey(key)) continue

    const lifted = LIFTED[key]
    if (lifted) {
      if (lifted !== 'event') {
        // Values arrive as strings over FCM; keep them as strings.
        ;(envelope as unknown as Record<string, string>)[lifted] = String(value)
      }
    } else if (key !== 'deeplink') {
      envelope.params[key] = String(value)
    }
  }

  // The native flat payload may carry a deeplink; use it as the action target only
  // when the backend didn't already provide action_url.
  if (!envelope.actionUrl && typeof raw.deeplink === 'string' && raw.deeplink) {
    envelope.actionUrl = raw.deeplink
  }

  return envelope
}
